<style lang="scss" scoped>
    ::v-deep {
        pre:not([class]) {
            white-space: pre-wrap;
            margin-bottom: 1.5em;
        }
    }

    .message {
        width: 100%;
        display: flex;
        gap: 12px;
        align-items: flex-end;
        flex-direction: column;
        padding: 20px 0;
        &.system {
            align-items: flex-start;
            flex-direction: row;
        }
        &.user {
            white-space: pre-wrap;

            & > div {
                background: #ddd;
                padding: 12px 24px;
                border-radius: 20px;
            }
        }
        &.system > div {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
        > div {
            max-width: 80%;
        }
        &-content {
            max-width: 100%;
            overflow: auto;

            ::v-deep{
                table {
                    th, td {
                        padding-top: 8px;
                        padding-bottom: 8px;
                        border-bottom: 1px solid lightgray;
                    }

                    th:not(:last-of-type),
                    td:not(:last-of-type) {
                        padding-right: 24px;
                    }
                }
            }
        }
    }
    .message-space {
        height: 0;
        visibility: hidden;
        margin-top: -20px;
    }
    .input {
        width: 100%;
        max-width: 768px;
        margin: 0 auto;
    }
    .my-editor {
        height: auto;
        background: #ddd;
        font-size: 14px;
        line-height: 1.5;
        margin-top: 8px;
        padding: 10px 16px;
        border-radius: 10px;
        margin-top: 8px;
    }
    .controls {
        display: flex;
        margin-top: 4px;
        justify-content: space-between;
    }
    .controls > div {
        display: flex;
        gap: 4px;
    }
    .pulse {
        opacity: 1;
        animation: pulse 3s 0s ease-in-out infinite;
    }
    @keyframes pulse {
        50% {
            opacity: .3;
        }
    }
    .score-tooltip {
        gap: 8px;
    }

    .message-content-scroll {
        width: 100%;
        max-width: 100%;
    }
    .message-content {
        overflow: visible;
    }
    .message-content::-webkit-scrollbar {
        display: none;
    }
</style>

<template>
    <div>
        <div class="message user">
            <div v-if="isManageMode || isNewMode">
                <div class="message-space">
                    {{ prompt }}
                </div>
                <v-textarea
                    v-model="prompt"
                    :placeholder="$t('copilot.userPrompt')"
                    rows="1"
                    auto-grow
                    hide-details />
            </div>
            <div v-else>
                {{ prompt }}
            </div>
        </div>
        <div class="message system">
            <img
                src="@/assets/logo-symbol.avif"
                :class="{ 'pulse': !message.chat.response }"
                height="32" />
            <div
                v-if="!message.chat.response"
                class="pulse font-weight-bold">
                . . .
            </div>
            <div v-else>
                <overlay-scrollbars
                    v-if="!isManageMode && !isNewMode"
                    class="message-content-scroll"
                    :options="{ overflowBehavior: { x: 'scroll' } }">
                    <markdown-renderer
                        class="message-content"
                        :content="message?.chat?.response?.content?.trim() || ''"
                        @run-query="runQuery" />
                </overlay-scrollbars>
                <div v-if="message.chat.response.sqlText?.length || message.chat.response.errorText?.length">
                    <v-tabs>
                        <v-tab v-if="message.chat.response.sqlText?.length">
                            SQL
                        </v-tab>
                        <v-tab v-if="message.chat.response.errorText?.length && !isManageMode && !isNewMode">
                            Error
                        </v-tab>
                        <v-tab-item v-if="message.chat.response.sqlText">
                            <prism-editor
                                v-model="sqlText"
                                class="code-editor"
                                :highlight="highlighter" />
                        </v-tab-item>
                        <v-tab-item v-if="message.chat.response.errorText">
                            {{ message.chat.response.errorText }}
                        </v-tab-item>
                    </v-tabs>
                </div>
                <div
                    v-if="isManageMode || isNewMode"
                    class="controls"
                    :class="{'hide': isNewMode}">
                    <div>
                        <v-btn
                            text
                            x-small
                            color="primary"
                            @click="toggleGoldenQuery">
                            <v-icon left>
                                mdi-bookmark-off
                            </v-icon>
                            {{ $t('copilot.removeGolden') }}
                        </v-btn>
                    </div>
                    <div
                        v-if="message.chat.response.sqlText?.length"
                        class="context">
                        <v-btn
                            text
                            x-small
                            class="btn-query-save"
                            color="primary"
                            @click="updateQuery">
                            <v-icon left>
                                mdi-content-save-outline
                            </v-icon>
                            {{ $t('save') }}
                        </v-btn>
                    </div>
                </div>
                <div
                    v-else
                    class="controls">
                    <div>
                        <v-btn
                            icon
                            x-small
                            :class="[{ 'success--text': message.feedback === 'LIKE' }]"
                            @click="submitFeedback('LIKE')">
                            <v-icon>mdi-thumb-up</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            x-small
                            :class="[{ 'error--text': message.feedback === 'DISLIKE' }]"
                            @click="submitFeedback('DISLIKE')">
                            <v-icon>mdi-thumb-down</v-icon>
                        </v-btn>
                        <v-tooltip
                            v-if="message.chat.response.sqlText?.length && selectedAgent.type === 'USER'"
                            max-width="300"
                            open-delay="400"
                            :open-on-hover="true"
                            :open-on-focus="false"
                            :open-on-click="false"
                            top>
                            <template #activator="{ on }">
                                <v-btn
                                    text
                                    x-small
                                    :color="messageQuery ? 'primary' : null"
                                    v-on="on"
                                    @click="toggleGoldenQuery">
                                    <v-icon left>
                                        {{ messageQuery ? 'mdi-bookmark' : 'mdi-bookmark-outline' }}
                                    </v-icon>
                                    {{ $t('copilot.markGolden') }}
                                </v-btn>
                            </template>
                            <div>
                                {{ $t('copilot.markGoldenInfo') }}
                            </div>
                        </v-tooltip>
                        <v-tooltip
                            v-if="canAnalyseQuery"
                            max-width="300"
                            open-delay="400"
                            :open-on-hover="true"
                            :open-on-focus="false"
                            :open-on-click="false"
                            :disabled="isAnalyseQueryActive"
                            top>
                            <template #activator="{ on }">
                                <div v-on="on">
                                    <v-btn
                                        text
                                        x-small
                                        color="primary"
                                        :loading="isAnalysingQuery"
                                        :disabled="!isAnalyseQueryActive"
                                        style="vertical-align: top;"
                                        @click="analyseQuery">
                                        <v-icon left>
                                            mdi-magnify-scan
                                        </v-icon>
                                        {{ $t('copilot.analyseQuery') }}
                                    </v-btn>
                                </div>
                            </template>
                            <div>
                                {{ $t('copilot.analyseQueryInfo') }}
                            </div>
                        </v-tooltip>
                        <v-tooltip
                            v-if="message.confidence"
                            max-width="300"
                            open-delay="400"
                            :open-on-hover="true"
                            :open-on-focus="false"
                            :open-on-click="false"
                            :disabled="!confidenceInfo.reason"
                            top>
                            <template #activator="{ on }">
                                <div v-on="on">
                                    <v-btn
                                        text
                                        x-small
                                        color="primary"
                                        style="vertical-align: top;"
                                        @click="showEvaluationResult">
                                        <v-icon left>
                                            mdi-magnify-scan
                                        </v-icon>
                                        {{ $t('copilot.evaluationResult') }}
                                    </v-btn>
                                </div>
                            </template>
                            <div>
                                {{ $t('copilot.clickToView') }}
                            </div>
                        </v-tooltip>
                    </div>
                    <div
                        v-if="message.chat.response.sqlText?.length"
                        class="context">
                        <v-btn
                            text
                            x-small
                            @click="copyToClipboard(sqlText)">
                            <v-icon left>
                                mdi-content-copy
                            </v-icon>
                            {{ $t('copy') }}
                        </v-btn>
                        <v-btn
                            text
                            x-small
                            color="primary"
                            :disabled="!hasSQLChanged"
                            @click="runQuery({ type: queryType, code: sqlText })">
                            <v-icon left>
                                mdi-play-box
                            </v-icon>
                            {{ $t('copilot.runQuery') }}
                            <span v-if="queryType === 'api'">
                                → {{ selectedDatasource?.name | truncate(20) }}
                            </span>
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>
        <MarkGoldenQueryModal
            ref="markGoldenQueryModal"
            :message="message" />
        <EvaluationResultModal
            v-if="message?.confidence"
            ref="evaluationResultModal"
            :result="message?.confidence" />
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import Prism from 'prismjs'
    import { PrismEditor } from 'vue-prism-editor'
    import MarkGoldenQueryModal from 'components/copilot/MarkGoldenQueryModal.vue'
    import MarkdownRenderer from 'components/copilot/MarkdownRenderer.vue'
    import EvaluationResultModal from 'components/copilot/EvaluationResultModal.vue'
    import { isCopilotAgent } from 'services/copilot.js'

    export default {
        name: 'CopilotChatMessage',
        components: {
            PrismEditor,
            MarkGoldenQueryModal,
            MarkdownRenderer,
            EvaluationResultModal,
        },
        props: {
            message: {
                type: Object,
                required: true,
            },
            sessionId: {
                type: String,
                required: true,
            },
            mode: {
                type: String,
                default: 'chat',
            },
        },
        data() {
            return {
                prompt: '',
                sqlText: '',
                isAnalysingQuery: false,
                chartOptions: {
                    chart: {
                        height: 280,
                        type: 'radialBar',
                    },
                    colors: ['green'],
                    plotOptions: {
                        radialBar: {
                            startAngle: -90,
                            endAngle: 90,
                            track: {
                                background: '#fff',
                                startAngle: -90,
                                endAngle: 90,
                                dropShadow: {
                                    enabled: true,
                                    top: -3,
                                    left: 0,
                                    blur: 4,
                                    opacity: 0.7,
                                },
                            },

                            dataLabels: {
                                show: true,
                                name: {
                                    offsetY: -30,
                                    show: true,
                                    color: '#fff',
                                    fontSize: '12px',
                                },
                                value: {
                                    formatter: function(val) {
                                        return parseInt(val) / 20
                                    },
                                    offsetY: -10,
                                    color: '#111',
                                    fontSize: '36px',
                                    show: true,
                                },
                            },
                        },
                    },
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shade: 'dark',
                            type: 'horizontal',
                            shadeIntensity: 0.5,
                            gradientToColors: ['green'],
                            stops: [0, 100],
                        },
                    },
                    stroke: {
                        lineCap: 'butt',
                    },
                    labels: [''],
                },
            }
        },
        computed: {
            ...mapGetters([
                'selectedAgent',
                'selectedChatSession',
                'selectedDatasource'
            ]),
            isManageMode() {
                return this.mode === 'manage'
            },
            isNewMode() {
                return this.mode === 'new'
            },
            hasSQLChanged() {
                return this.sqlText !== this.message?.chat?.response?.sqlText?.trim()
            },
            hasPromptChanged() {
                return this.prompt !== this.message?.chat?.prompt?.trim()
            },
            messageQuery() {
                return this.selectedAgent.queries.find(query => query.originEntryId === this.message.id)
            },
            canAnalyseQuery() {
                // Keep on dev/test for now
                return this.$unleash.isFeatureEnabled('enable-genai-evaluate-query') &&
                    this.message.chat.response.sqlText?.length &&
                    this.selectedAgent.type === 'USER' &&
                    !this.messageQuery &&
                    !this.message.confidence
            },
            isAnalyseQueryActive() {
                return this.selectedAgent?.queries?.length > 0
            },
            confidenceInfo() {
                const info = {
                    reason: null,
                    label: null,
                    color: null,
                }
                if (this.message?.confidence) {
                    info.reason = this.message?.confidence?.reason
                    if (this.message?.confidence?.confidence >= 4) {
                        info.label = this.$t('copilot.confidence.high')
                        info.color = 'success'
                    } else if (this.message?.confidence?.confidence >= 2) {
                        info.label = this.$t('copilot.confidence.med')
                        info.color = 'warning'
                    } else {
                        info.label = this.$t('copilot.confidence.low')
                        info.color = 'error'
                    }
                }
                return info
            },
            series() {
                return [100/5 * (this.message?.confidence?.confidence || 0)]
            },
            queryType() {
                return isCopilotAgent('ama', this.selectedAgent?.id) ? 'api' : 'msg'
            },
        },
        watch: {
            'message.chat.response.sqlText': {
                handler() {
                    this.sqlText = this.message?.chat?.response?.sqlText?.trim() || ''
                },
                immediate: true,
            },
            'message.chat.prompt': {
                handler() {
                    this.prompt = this.message?.chat?.prompt?.trim() || ''
                },
                immediate: true,
            },
            'message.confidence.confidence': {
                handler() {
                    this.chartOptions.labels = [this.confidenceInfo?.label]
                    if (this.message?.confidence?.confidence >= 4) {
                        this.chartOptions.colors = ['lightgreen']
                        this.chartOptions.fill.gradient.gradientToColors = ['lightgreen']
                    } else if (this.message?.confidence?.confidence >= 2) {
                        this.chartOptions.colors = ['orange']
                        this.chartOptions.fill.gradient.gradientToColors = ['orange']
                    } else {
                        this.chartOptions.colors = ['red']
                        this.chartOptions.fill.gradient.gradientToColors = ['red']
                    }
                },
                immediate: true,
            },
        },
        methods: {
            ...mapActions([
                'submitChatFeedback',
                'addAgentQuery',
                'updateAgentQuery',
                'removeAgentQuery',
                'clearTempAgentQueries',
                'refreshAgents',
                'analyseChatMessageQuery',
                'copyToClipboard'
            ]),
            highlighter(code) {
                const lang = 'sql'
                return Prism.highlight(code, Prism.languages[lang], lang)
            },
            runQuery(data) {
                this.$emit('runQuery', data)
            },
            showEvaluationResult() {
                this.$refs.evaluationResultModal.open()
            },
            async updateQuery() {
                if (this.isNewMode && !this.hasSQLChanged && !this.hasPromptChanged) {
                    return false
                }
                if (!this.sqlText.length || !this.prompt.length) {
                    this.$store.commit('showMessage', { text: this.$t('errors.enterQueryInfo'), type: this.$config.messageType.ERROR, })
                    return false
                }
                const chat = this.$lodash.cloneDeep(this.message.chat)
                chat.response.sqlText = this.sqlText.trim()
                chat.prompt = this.prompt.trim()
                if (this.message.id) {
                    const params = {
                        agentId: this.selectedAgent.id,
                        queryId: this.isNewMode ? this.messageQuery.id : this.message.id,
                        query: {
                            chat,
                            originEntryId: null,
                        },
                    }
                    await this.updateAgentQuery(params)
                } else {
                    await this.addAgentQuery({
                        agentId: this.selectedAgent.id,
                        type: 'GOLDEN',
                        chat,
                    })
                    await this.removeAgentQuery({
                        agentId: this.selectedAgent.id,
                        tempQueryId: this.message?.tempId,
                    })
                }
            },
            async submitFeedback(feedback) {
                await this.submitChatFeedback({
                    feedback: this.message.feedback === feedback ? null : feedback,
                    entryId: this.message.id,
                    chatSessionId: this.sessionId,
                })
            },
            async toggleGoldenQuery() {
                if (this.isManageMode || this.messageQuery) {
                    await this.removeAgentQuery({
                        agentId: this.selectedAgent.id,
                        queryId: this.isManageMode ? this.message.id : this.messageQuery.id,
                        tempQueryId: this.message?.tempId,
                    })
                } else {
                    this.$refs.markGoldenQueryModal.open()
                }
                this.refreshAgents()
            },
            async analyseQuery() {
                this.isAnalysingQuery = true
                await this.analyseChatMessageQuery({
                    chatSessionId: this.selectedChatSession?.id,
                    entryId: this.message.id,
                })
                this.isAnalysingQuery = false
            },
        },
    }
</script>
